import { useState } from 'react';
import {
  callLLMWithBackoff,
  parseAIResponse,
  parseAIArrayResponse,
  validateNodeData,
} from '../utils/ai';
import { generateSystemPrompt } from '../prompts/nodeCreation';
import { generateSolutionsPrompt } from '../prompts/solutions';
import { generateEnhancePrompt } from '../prompts/enhance';
import { generateInfoGatheringPrompt, generateSynthesisPrompt } from '../prompts/infoGathering';
import { generateConversationalPrompt, parseConversationalResponse } from '../prompts/conversational';
import { generateAnalyzeMapPrompt, parseReportResponse } from '../prompts/analysis';
import { 
  FLASHCARD_SYSTEM_PROMPT, constructFlashcardUserPrompt, parseFlashcardResponse,
  CONTENT_SYSTEM_PROMPT, constructContentUserPrompt 
} from '../prompts/flashcard';

export const useLLMProvider = (provider, lang = 'en') => {
  const [isLoading, setIsLoading] = useState(false);

  const sendMessage = async (userMessage, hasProblems) => {
    if (!provider) throw new Error('Provider non configurato');
    setIsLoading(true);
    try {
      const systemPrompt = generateSystemPrompt(hasProblems);
      const response = await callLLMWithBackoff(provider, systemPrompt, userMessage);
      const jsonData = parseAIResponse(response);
      const validation = validateNodeData(jsonData);
      return { response, jsonData, validation };
    } finally {
      setIsLoading(false);
    }
  };

  const suggestSolutions = async (problemNode, existingSolutions, relatedNodes = []) => {
    if (!provider) throw new Error('Provider non configurato');
    setIsLoading(true);
    try {
      const prompt = generateSolutionsPrompt(problemNode, existingSolutions, relatedNodes, lang);
      const response = await callLLMWithBackoff(provider, '', prompt);
      return parseAIArrayResponse(response);
    } finally {
      setIsLoading(false);
    }
  };

  const enhanceSolution = async (node, type) => {
    if (!provider) throw new Error('Provider non configurato');
    setIsLoading(true);
    try {
      const prompt = generateEnhancePrompt(node, type, lang);
      const response = await callLLMWithBackoff(provider, '', prompt);
      return response;
    } finally {
      setIsLoading(false);
    }
  };

  const generateNextQuestion = async (node, collectedInfo) => {
    if (!provider) throw new Error('Provider non configurato');
    setIsLoading(true);
    try {
      const prompt = generateInfoGatheringPrompt(node, collectedInfo, lang);
      const response = await callLLMWithBackoff(provider, '', prompt);
      const jsonData = parseAIResponse(response);
      return jsonData?.question || null;
    } finally {
      setIsLoading(false);
    }
  };

  const synthesizeDescription = async (title, summary, collectedInfo) => {
    if (!provider) throw new Error('Provider non configurato');
    setIsLoading(true);
    try {
      const prompt = generateSynthesisPrompt(title, summary, collectedInfo, lang);
      const response = await callLLMWithBackoff(provider, '', prompt);
      return response.trim();
    } finally {
      setIsLoading(false);
    }
  };

  const sendConversationalMessage = async (
    userMessage,
    mapJson,
    conversationHistory = [],
    exchangesWithoutNodes = 0,
    isUnnamed = false,
    isEmpty = false,
    mode = 'brainstormer',
    userContext = '',
    mapContext = ''
  ) => {
    if (!provider) throw new Error('Provider non configurato');
    setIsLoading(true);
    try {
      const systemPrompt = generateConversationalPrompt(mapJson, exchangesWithoutNodes, isUnnamed, isEmpty, mode, userContext, mapContext, lang);
      const messages = [
        ...conversationHistory
          .filter(m => !m.isInfoGathering)
          .map(m => ({ role: m.role === 'user' ? 'user' : 'assistant', content: m.content })),
        { role: 'user', content: userMessage }
      ];
      const response = await provider.chatMultiTurn(messages, systemPrompt);
      return parseConversationalResponse(response);
    } finally {
      setIsLoading(false);
    }
  };

  const improveContext = async (rawText, type = 'user') => {
    if (!provider) throw new Error('Provider non configurato');
    const typeHint = type === 'user'
      ? 'profilo utente (ruolo, background, preferenze di comunicazione)'
      : 'contesto di progetto (obiettivo, stakeholder, vincoli specifici)';
    const prompt = `Trasforma questo testo grezzo in un contesto strutturato e ottimizzato da iniettare nel system prompt di un AI assistant per sessioni di brainstorming.

Tipo: ${typeHint}

Linee guida:
- Usa la terza persona ("L'utente è...", "Il progetto riguarda...")
- 3-5 frasi concise e dense di informazione
- Evidenzia elementi utili all'AI: ruolo, obiettivi, vincoli, preferenze
- Elimina ridondanze e vaghezze

Testo grezzo:
"${rawText}"

Restituisci SOLO il contesto migliorato, senza commenti o introduzioni.`;
    const response = await callLLMWithBackoff(provider, '', prompt);
    return response.trim();
  };

  const analyzeMap = async (mapJson) => {
    if (!provider) throw new Error('Provider non configurato');
    setIsLoading(true);
    try {
      const prompt = generateAnalyzeMapPrompt(mapJson);
      const response = await callLLMWithBackoff(provider, '', prompt);
      return parseReportResponse(response);
    } finally {
      setIsLoading(false);
    }
  };

  const generateFlashcard = async (node, neighbors, currentSummary, rootNode) => {
    if (!provider) throw new Error('Provider non configurato');
    setIsLoading(true);
    try {
      const prompt = constructFlashcardUserPrompt(node, neighbors, currentSummary, rootNode);
      const response = await callLLMWithBackoff(provider, FLASHCARD_SYSTEM_PROMPT, prompt);
      return parseFlashcardResponse(response, {
        title: node?.title || '',
        summary: currentSummary || node?.summary || '',
        description: node?.description || '',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const generateNodeContent = async (node, neighbors, rootNode) => {
    if (!provider) throw new Error('Provider non configurato');
    setIsLoading(true);
    try {
      const prompt = constructContentUserPrompt(node, neighbors, rootNode);
      const response = await callLLMWithBackoff(provider, CONTENT_SYSTEM_PROMPT, prompt);
      return response.trim();
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    sendMessage,
    sendConversationalMessage,
    suggestSolutions,
    enhanceSolution,
    generateNextQuestion,
    synthesizeDescription,
    analyzeMap,
    generateFlashcard,
    generateNodeContent,
    improveContext,
  };
};
